// src/i18n.ts
var DEFAULT_STORAGE_KEY = "i18n:locale";
function flattenResources(resources, prefix = "") {
  const result = {};
  for (const [key, value] of Object.entries(resources)) {
    const fullKey = prefix ? `${prefix}.${key}` : key;
    if (typeof value === "string") {
      result[fullKey] = value;
    } else {
      Object.assign(result, flattenResources(value, fullKey));
    }
  }
  return result;
}
function escapeHtml(str) {
  const htmlEscapes = {
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    '"': "&quot;",
    "'": "&#39;"
  };
  return str.replace(/[&<>"']/g, (char) => htmlEscapes[char]);
}
function defaultPluralization(count, locale) {
  const lang = locale.split("-")[0];
  switch (lang) {
    case "zh":
    case "ja":
    case "ko":
      return "";
    case "ar":
      if (count === 0) return "_zero";
      if (count === 1) return "_one";
      if (count === 2) return "_two";
      if (count >= 3 && count <= 10) return "_few";
      if (count >= 11 && count <= 99) return "_many";
      return "_other";
    default:
      if (count === 0) return "_zero";
      if (count === 1) return "_one";
      return "_other";
  }
}
function detectBrowserLocale(supportedLocales) {
  if (typeof navigator === "undefined") return null;
  const browserLocales = navigator.languages ?? [navigator.language];
  for (const browserLocale of browserLocales) {
    if (supportedLocales.includes(browserLocale)) {
      return browserLocale;
    }
    const lang = browserLocale.split("-")[0];
    const match = supportedLocales.find(
      (l) => l === lang || l.startsWith(`${lang}-`)
    );
    if (match) {
      return match;
    }
  }
  return null;
}
var I18nModule = class {
  name = "i18n";
  context;
  config;
  resources = /* @__PURE__ */ new Map();
  currentLocale;
  loading = /* @__PURE__ */ new Set();
  storageKey;
  constructor(config) {
    this.config = {
      cacheResources: true,
      detectLocale: true,
      interpolation: {
        prefix: "{{",
        suffix: "}}",
        escapeHtml: true
      },
      ...config
    };
    this.currentLocale = config.defaultLocale;
    this.storageKey = config.storageKey ?? DEFAULT_STORAGE_KEY;
  }
  /**
   * Initialize the i18n module
   */
  async init(context) {
    this.context = context;
    const detected = await this.detectLocale();
    this.currentLocale = detected.locale;
    await this.loadLocale(this.currentLocale);
    context.events.emit("i18n:ready", {
      locale: this.currentLocale,
      source: detected.source
    });
  }
  /**
   * Translate a key
   */
  t(key, options) {
    const resources = this.resources.get(this.currentLocale);
    const fallbackResources = this.config.fallbackLocale ? this.resources.get(this.config.fallbackLocale) : void 0;
    let actualKey = key;
    if (options?.context) {
      actualKey = `${key}_${options.context}`;
    }
    if (options?.count !== void 0) {
      const pluralFn = this.config.pluralization ?? defaultPluralization;
      const pluralSuffix = pluralFn(options.count, this.currentLocale);
      const pluralKey = `${actualKey}${pluralSuffix}`;
      if (resources?.[pluralKey] || fallbackResources?.[pluralKey]) {
        actualKey = pluralKey;
      }
    }
    let value = resources?.[actualKey] ?? fallbackResources?.[actualKey] ?? options?.defaultValue ?? key;
    if (options) {
      value = this.interpolate(value, options);
    }
    return value;
  }
  /**
   * Change the current locale
   */
  async setLocale(locale) {
    if (!this.config.supportedLocales.includes(locale)) {
      throw new Error(`Locale "${locale}" is not supported`);
    }
    if (locale === this.currentLocale) {
      return;
    }
    await this.loadLocale(locale);
    const previousLocale = this.currentLocale;
    this.currentLocale = locale;
    await this.context.storage.set(this.storageKey, locale);
    this.context.events.emit("i18n:localeChanged", {
      locale,
      previousLocale
    });
  }
  /**
   * Get the current locale
   */
  getLocale() {
    return this.currentLocale;
  }
  /**
   * Get all supported locales
   */
  getSupportedLocales() {
    return [...this.config.supportedLocales];
  }
  /**
   * Check if a locale is supported
   */
  isSupported(locale) {
    return this.config.supportedLocales.includes(locale);
  }
  /**
   * Check if resources for a locale are loaded
   */
  isLoaded(locale) {
    return this.resources.has(locale);
  }
  /**
   * Check if a locale is currently loading
   */
  isLoading(locale) {
    return this.loading.has(locale);
  }
  /**
   * Preload resources for a locale
   */
  async preload(locale) {
    if (!this.config.supportedLocales.includes(locale)) {
      throw new Error(`Locale "${locale}" is not supported`);
    }
    await this.loadLocale(locale);
  }
  /**
   * Add additional resources for a locale
   */
  addResources(locale, resources) {
    const flattened = typeof Object.values(resources)[0] === "string" ? resources : flattenResources(resources);
    const existing = this.resources.get(locale) ?? {};
    this.resources.set(locale, { ...existing, ...flattened });
  }
  /**
   * Check if a key exists
   */
  exists(key, locale) {
    const loc = locale ?? this.currentLocale;
    const resources = this.resources.get(loc);
    return resources?.[key] !== void 0;
  }
  /**
   * Get a translation resource by key (without interpolation)
   */
  getResource(key, locale) {
    const loc = locale ?? this.currentLocale;
    return this.resources.get(loc)?.[key];
  }
  /**
   * Cleanup on destroy
   */
  async destroy() {
    if (!this.config.cacheResources) {
      this.resources.clear();
    }
  }
  // Private methods
  async detectLocale() {
    const savedLocale = await this.context.storage.get(this.storageKey);
    if (savedLocale && this.config.supportedLocales.includes(savedLocale)) {
      return { locale: savedLocale, source: "storage" };
    }
    if (this.config.detectLocale) {
      const browserLocale = detectBrowserLocale(this.config.supportedLocales);
      if (browserLocale) {
        return { locale: browserLocale, source: "browser" };
      }
    }
    return { locale: this.config.defaultLocale, source: "default" };
  }
  async loadLocale(locale) {
    if (this.resources.has(locale)) {
      return;
    }
    if (this.loading.has(locale)) {
      return new Promise((resolve) => {
        const checkLoaded = setInterval(() => {
          if (!this.loading.has(locale)) {
            clearInterval(checkLoaded);
            resolve();
          }
        }, 50);
      });
    }
    this.loading.add(locale);
    this.context.events.emit("i18n:loadStart", { locale });
    try {
      const resources = await this.config.loadResources(locale);
      const flattened = typeof Object.values(resources)[0] === "string" ? resources : flattenResources(resources);
      this.resources.set(locale, flattened);
      this.context.events.emit("i18n:loadComplete", { locale });
    } catch (error) {
      this.context.events.emit("i18n:loadError", { locale, error });
      throw error;
    } finally {
      this.loading.delete(locale);
    }
  }
  interpolate(value, options) {
    const { prefix, suffix, escapeHtml: shouldEscape } = this.config.interpolation;
    const escapedPrefix = prefix.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const escapedSuffix = suffix.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const regex = new RegExp(`${escapedPrefix}\\s*(\\w+)\\s*${escapedSuffix}`, "g");
    return value.replace(regex, (match, key) => {
      if (key in options) {
        const val = String(options[key]);
        return shouldEscape ? escapeHtml(val) : val;
      }
      return match;
    });
  }
};

export { I18nModule };
//# sourceMappingURL=index.mjs.map
//# sourceMappingURL=index.mjs.map